import { useTranslation } from "react-i18next";
import { ITEM_KINDS } from "../../../api/content/reviewDecoders";
import type { PlannedInput } from "../../../api/content/stepPlans";
import ResultPicker from "../../knowledgeData/results/ResultPicker";
import { LibraryPicker } from "../../search";
import { pickViewsOf } from "../../workflows/presentation/resolvePresentation";
import ValueEditor from "./ValueEditor";
import type { PickedIn } from "./valuePicking";

interface PlannedValueFieldProps {
  input: PlannedInput;
  /** The value given or prefilled; `null` or `undefined` for none. */
  value: unknown;
  onChange: (value: unknown) => void;
  pickedIn: PickedIn;
  /** The name of the item an item input holds, where it holds one. */
  itemName: string | null;
}

/**
 * The field that gives one step input a value, by its kind (ADR-0032 D7, D8): an item picked
 * in the library (F38), a recorded result picked among those the person may decide (A8), or
 * else the value typed. A step run, a Run's start and the Workflow editor's defaults (WD-1+3
 * S4-G1) give values with it; module B checks the value against the input's types.
 */
export default function PlannedValueField({ input, value, onChange, pickedIn, itemName }: PlannedValueFieldProps) {
  const { t } = useTranslation();
  const pickIn = input.takesItem ? pickedIn.items : undefined;
  const pickResultIn = pickViewsOf(input.accepts, input.lineages).has("result") ? pickedIn.results : undefined;
  const reference = input.takesItem && typeof value === "string" ? value : null;
  return (
    <>
      {pickIn === undefined ? null : (
        <LibraryPicker
          projectId={pickIn}
          // The kinds module B says may fill it; a project subtype is checked on the version when the step runs.
          kinds={input.itemKinds.length === 0 ? ITEM_KINDS : input.itemKinds}
          // The item it holds, picked here or prefilled, by name.
          buttonLabel={reference === null ? t("threads.current.inputs.choose") : (itemName ?? reference)}
          buttonAccessibleName={t("threads.current.inputs.chooseFor", { name: input.label })}
          searchLabel={t("threads.current.inputs.chooseSearch")}
          onPick={(picked) => {
            onChange(picked.reference);
          }}
        />
      )}
      {pickResultIn !== undefined ? (
        <ResultPicker projectId={pickResultIn} label={input.label} value={value} required={input.required} onPick={onChange} />
      ) : pickIn !== undefined && input.accepts.length === 1 ? null : (
        // An item input that also takes another type keeps its field beside the picker.
        <ValueEditor label={input.label} accepts={input.accepts} lineages={input.lineages} value={value} onChange={onChange} />
      )}
    </>
  );
}
